import { Injectable } from '@angular/core';
import { IAMPolicyPasswordComplexity, LookupProxy } from '@/app/proxy/proxies';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class PasswordService {
  private passwordRuleset = new BehaviorSubject<IAMPolicyPasswordComplexity | null>(null);

  constructor(
    private _lookupProxy: LookupProxy,
    private _translateService: TranslateService
  ) {
    this.loadPasswordRuleset();
  }

  private loadPasswordRuleset(): void {
    this._lookupProxy.lookup_AppStartupLookups().subscribe((response) => {
      this.passwordRuleset.next(response.result.passwordPolicy);
    });
  }

  getPasswordRuleset(): Observable<IAMPolicyPasswordComplexity> {
    return this.passwordRuleset.asObservable().pipe(map((ruleset) => ruleset || new IAMPolicyPasswordComplexity()));
  }

  checkPasswordIsValid(password: string): boolean {
    const currentRuleset = this.passwordRuleset.value;
    if (!currentRuleset) {
      return false;
    }

    // Check if password exists and meets length requirements
    if (
      !password ||
      (currentRuleset.minimumCharacters && password.length < currentRuleset.minimumCharacters) ||
      (currentRuleset.maximumLength && password.length > currentRuleset.maximumLength)
    ) {
      return false;
    }

    // Check uppercase letters requirement
    if (currentRuleset.uppercaseLetters) {
      const uppercaseCount = (password.match(/[A-Z]/g) || []).length;
      if (uppercaseCount < currentRuleset.uppercaseLetters) {
        return false;
      }
    }

    // Check lowercase letters requirement
    if (currentRuleset.lowercaseLetters) {
      const lowercaseCount = (password.match(/[a-z]/g) || []).length;
      if (lowercaseCount < currentRuleset.lowercaseLetters) {
        return false;
      }
    }

    // Check numbers requirement
    if (currentRuleset.numbers) {
      const numbersCount = (password.match(/[0-9]/g) || []).length;
      if (numbersCount < currentRuleset.numbers) {
        return false;
      }
    }

    // Check special characters requirement
    if (currentRuleset.specialCharacters === true) {
      const specialCharsRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/;
      const specialCharCount = (password.match(specialCharsRegex) || []).length;
      if (specialCharCount < 1) {
        return false;
      }
    }

    return true;
  }

  getPasswordValidationErrors(password: string): string[] {
    const errors: string[] = [];
    if (!password) {
      return [this._translateService.instant('doktar_app.auth.register.create_password.error_required')];
    }

    const policy = this.passwordRuleset.value;

    if (!policy) {
      console.log('No password policy loaded');
      window.location.reload();
      return [this._translateService.instant('doktar_app.auth.register.create_password.error_policy_not_available')];
    }

    // Check length requirements
    if (policy.minimumCharacters && password.length < policy.minimumCharacters) {
      errors.push(
        this._translateService.instant('doktar_app.auth.register.create_password.error_min_length', {
          minLength: policy.minimumCharacters,
        })
      );
    }
    if (policy.maximumLength && password.length > policy.maximumLength) {
      errors.push(
        this._translateService.instant('doktar_app.auth.register.create_password.error_max_length', {
          maxLength: policy.maximumLength,
        })
      );
    }

    // Check uppercase letters requirement
    if (policy.uppercaseLetters) {
      const uppercaseCount = (password.match(/[A-Z]/g) || []).length;
      if (uppercaseCount < policy.uppercaseLetters) {
        errors.push(
          this._translateService.instant('doktar_app.auth.register.create_password.error_uppercase', {
            uppercaseLetters: policy.uppercaseLetters,
          })
        );
      }
    }

    // Check lowercase letters requirement
    if (policy.lowercaseLetters) {
      const lowercaseCount = (password.match(/[a-z]/g) || []).length;
      if (lowercaseCount < policy.lowercaseLetters) {
        errors.push(
          this._translateService.instant('doktar_app.auth.register.create_password.error_lowercase', {
            lowercaseLetters: policy.lowercaseLetters,
          })
        );
      }
    }

    // Check numbers requirement
    if (policy.numbers) {
      const numbersCount = (password.match(/[0-9]/g) || []).length;
      if (numbersCount < policy.numbers) {
        errors.push(
          this._translateService.instant('doktar_app.auth.register.create_password.error_numbers', {
            numbers: policy.numbers,
          })
        );
      }
    }

    // Check special characters requirement
    if (policy.specialCharacters) {
      const specialCharsRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/;
      const specialCharCount = (password.match(specialCharsRegex) || []).length;
      if (specialCharCount < 1) {
        errors.push(this._translateService.instant('doktar_app.auth.register.create_password.error_special_chars'));
      }
    }

    return errors;
  }

  // Debug method to show current password policy
  getCurrentPasswordPolicy(): IAMPolicyPasswordComplexity | null {
    return this.passwordRuleset.value;
  }

  // Debug method to get detailed validation info
  getDetailedValidationInfo(password: string): any {
    const policy = this.passwordRuleset.value;
    if (!policy) {
      return { error: 'No password policy loaded' };
    }

    return {
      policy: policy,
      password: password,
      checks: {
        length: {
          required: policy.minimumCharacters,
          max: policy.maximumLength,
          actual: password?.length || 0,
          valid:
            password &&
            (!policy.minimumCharacters || password.length >= policy.minimumCharacters) &&
            (!policy.maximumLength || password.length <= policy.maximumLength),
        },
        uppercase: {
          required: policy.uppercaseLetters,
          actual: (password?.match(/[A-Z]/g) || []).length,
          valid: !policy.uppercaseLetters || (password?.match(/[A-Z]/g) || []).length >= policy.uppercaseLetters,
        },
        lowercase: {
          required: policy.lowercaseLetters,
          actual: (password?.match(/[a-z]/g) || []).length,
          valid: !policy.lowercaseLetters || (password?.match(/[a-z]/g) || []).length >= policy.lowercaseLetters,
        },
        numbers: {
          required: policy.numbers,
          actual: (password?.match(/[0-9]/g) || []).length,
          valid: !policy.numbers || (password?.match(/[0-9]/g) || []).length >= policy.numbers,
        },
        specialChars: {
          required: policy.specialCharacters ? 1 : 0,
          actual: (password?.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/) || []).length,
          valid:
            !policy.specialCharacters || (password?.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/) || []).length >= 1,
        },
      },
    };
  }
}

export function providePasswordDI() {
  return [PasswordService, LookupProxy];
}
